import { useState } from "react";

import clsx from "clsx";
import { useKBar } from "kbar";
import { isMacOs } from "react-device-detect";

import { useUser } from "@components/user-context";
import { useCommandPaletteContext } from "@features/command-palette/hooks";
import { useNorthbeamQuery } from "@hooks/http/use-northbeam-query";
import { NAVBAR_QUERY } from "@north-beam/nb-client-side";
import { type Navbar as NavbarQuery } from "@north-beam/nb-client-side";
import { ProcessingModal } from "@pages/attribution/processing-modal/processing-modal";

import { NotificationBellWithMenu } from "../notification-bell-with-menu";

export const CommandPalette = () => {
  const { user } = useUser();

  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);

  const { data } = useNorthbeamQuery<NavbarQuery>(NAVBAR_QUERY, {
    pollInterval: 60_000, // every 1 mins
    doNotRaiseErrorOnFailure: true,
  });
  const numIncidents = data?.me?.activeIncidentCount ?? 0;
  const numMaintenanceItems = data?.me?.activeAccountMaintenanceItemCount ?? 0;

  const { query } = useKBar();
  const { unregisterActionCallbacks } = useCommandPaletteContext();

  if (!user.featureFlags.enableCommandPalette) {
    return <div></div>;
  }

  return (
    <div
      className={clsx(
        "text-sm border border-v2-tertiary rounded-md py-3 px-2 w-1/2 text-v2-secondary h-[48px]",
        "flex items-center justify-between cursor-pointer",
      )}
      onClick={() => {
        if (isProcessingModalOpen) {
          return;
        }
        unregisterActionCallbacks();
        query.toggle();
      }}
    >
      <span className="truncate">
        <i className="text-base fa-regular fa-sharp fa-magnifying-glass ml-1 mr-2.5"></i>
        Type a command or search Northbeam
      </span>
      <span className="flex items-center gap-1">
        <NotificationBellWithMenu
          dotClassName="bg-v2-action-negative"
          className="text-v2-icon-secondary"
          numIncidents={numIncidents}
          numMaintenanceItems={numMaintenanceItems}
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsProcessingModalOpen(true);
          }}
          className="cursor-pointer"
        >
          <i
            className={clsx(
              "fa-regular fa-sharp fa-clock cursor-pointer transform",
              "transition-colors ease-in hover:bg-v2-highlight-information hover:text-v2-action ui-open:bg-cosmic-4 rounded-lg p-2",
            )}
          />
        </button>
        {isMacOs ? (
          <span
            className={clsx(
              "text-xs border border-v2-tertiary bg-v2-action-tertiary rounded px-[6px] py-[4px]",
              "flex items-center justify-center font-semibold",
            )}
          >
            <i className="fa-regular fa-sharp fa-command mr-1"></i>K
          </span>
        ) : (
          <span
            className={clsx(
              "text-xs border border-v2-tertiary bg-v2-action-tertiary rounded px-[6px] py-[4px]",
              "flex items-center justify-center font-semibold",
            )}
          >
            Ctrl K
          </span>
        )}
      </span>
      <ProcessingModal
        isOpen={isProcessingModalOpen}
        onClose={() => {
          setIsProcessingModalOpen(false);
        }}
      />
    </div>
  );
};
